import { Component, ElementRef, Renderer, OnInit, ViewChild, Inject, PLATFORM_ID, OnDestroy, Optional } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { isPlatformServer, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import { ProductService } from '../services/product.service'
import { CurrencyService } from '../shared/currency.service';
import { AnalyticsUtilsService } from '../shared/analytics-utils.service';

import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Product } from '../models/product.model';
import { CheckAvailability } from '../models/check-availability.model';
import { AppSettings } from '../settings/app.settings';
import { HrefLang, HrefLangDefinition } from '../shared/hreflang.service';
import { LoadingComponent } from '../shared/components/loading/loading.component';
import { CheckingUrl } from '../shared/checking-url.service';
import { DeviceService } from '../shared/device.service';
import { ScrollService } from '../shared/scroll-service';
import { SEOService } from '../shared/seo-service';
import { WindowScrollService } from '../shared/window-sroll.service';
import { isPlatformBrowser } from '@angular/common';

import { RESPONSE } from '@nguniversal/express-engine/tokens';



@Component({
  selector: 'product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss']
})
export class ProductComponent implements OnInit, OnDestroy {
  isNode: boolean;
  public product: Product;
  public id: number;
  public options: any;
  public check: CheckAvailability;
  public actualCurrency: number;
  public highPrice: number;
  public galleryIsHidden: boolean = true;
  public images: string[] = [];
  public imagesMicro: string[] = [];
  public img: string;
  public imagesHeader: string[] = [];
  public imagesHeaderMicro: string[] = [];
  public currentIndex: number = 0;
  public isAnimated: boolean = true;
  public animationNext: boolean = false;
  public animationPrev: boolean = false;
  public animationMin: boolean = false;
  public animationMax: boolean = false;
  public mouseOvered: boolean;
  public componentReady: boolean = false;
  public showingAnnounce: boolean = false;
  public notAvailable: boolean = false;
  public cruiseIsOpened: boolean = false;
  public isReady: boolean = false;
  public showMenuButton: boolean = false;
  public showSeeOptions: boolean = false;
  public productPriceHeight: number;
  public initialOffsetFromTop: number = 0;
  public initialOffsetFromTop2: number = 0;
  public deviceName: string;
  public mapOffset: number;
  public fixedViewportOffset: number;
  public scrollSubscription: Subscription = null;
  private SWIPE_ACTION = { LEFT: 'swipeleft', RIGHT: 'swiperight' };
  private structuredDataReady: boolean = false;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  @ViewChild(LoadingComponent) loadingComponent: LoadingComponent;
  @ViewChild('spinnerOptions') _spinner: LoadingComponent;

  constructor(private productService: ProductService,
    private route: ActivatedRoute, private router: Router, private location: Location, public currencyService: CurrencyService,
    private element: ElementRef, private renderer: Renderer, private meta: Meta, private title: Title,
    private analytics: AnalyticsUtilsService, private hlang: HrefLang, private seoService: SEOService, private scrollService: ScrollService,
    private checkUrl: CheckingUrl, private deviceService: DeviceService, private windowScroll: WindowScrollService,
    @Inject(PLATFORM_ID) private platformId: Object, @Optional() @Inject(RESPONSE) private response: any) {
    this.isNode = isPlatformServer(this.platformId);
  };

  ngOnInit() {
    this.route.params
      .subscribe(params => {
        this.id = +params['id'];
        this.getHreflangs(this.id);
        this.initFunction(this.id);
      });
    this.currencyService.currencyUpdated.pipe(takeUntil(this.ngUnsubscribe)).subscribe(currency => {
      if ((this.actualCurrency !== currency) && this.actualCurrency) {
        this.initFunction(this.id);
      }

    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private initFunction(id) {
    this.getProduct(id);
    this.getActualCurrency(this.currencyService.currencyStr);
    if (this.options) {
      this.checkAvailability(this.check);
    }
    this.isReady = true;
    this.showMenuButton = false;
    this.showSeeOptions = false;
    this.deviceName = this.deviceService.device;
    if (isPlatformBrowser(this.platformId)) {
      this.scrollSubscription = this.windowScroll.scroll$
        .subscribe(this.handleScroll.bind(this));
    }
  }

  scrollToInfo(id: string) {
    let posX = this.element.nativeElement.querySelector('#' + id).getBoundingClientRect().top;
    if (id != '#summary') {
      this.scrollService.scrollTo(posX - 124);
    } else {
      this.showMenuButton = false;
      this.scrollService.scrollTo(posX - 160);
    }
  }

  private getActualCurrency(currency) {
    if (currency === "EUR") {
      this.actualCurrency = 1;
    } else if (currency === "GBP") {
      this.actualCurrency = 2;
    } else {
      this.actualCurrency = 3;
    }
  }


  private getProduct(id: number) {
    this.productService.getProduct(id)
      .subscribe(_product => {
        let product = JSON.parse(JSON.stringify(_product));
        this.seoService.updateTitle(product.metaTitle || `${product.name} | City Expert ${product.provider.location}`);
        let desc: MetaDefinition = {
          name: 'description',
          content: product.metaDescription || product.description
        };
        this.seoService.updateDescription(desc)
        if (!product.isAvailable) {
          this.notAvailable = true;
        }
        product.header = `${this.deviceService.constructCloudfrontURL(AppSettings.CSS_CE_IMAGES, 'LARGE')}/${product.header}`;
        this.images = product.gallery.map(val => {
          return `${this.deviceService.constructCloudfrontURL(AppSettings.CSS_CE_IMAGES, 'LARGE')}/${val.name}`;
        });

        this.imagesMicro = product.gallery.map(val => {
          return `${this.deviceService.constructCloudfrontURL(AppSettings.CSS_CE_IMAGES, 'MICRO')}/${val.name}`;
        });

        if (this.deviceService.device === "desktop" || this.deviceService.device === "tablet") {
          this.imagesHeader = this.images.slice(0, 4);
          this.imagesHeaderMicro = this.imagesMicro.slice(0, 4);
        } else {
          this.imagesHeader = this.images.slice(0, 2);
          this.imagesHeaderMicro = this.imagesMicro.slice(0, 2);
        }

        this.product = product;

        if (this.product.bestPriceInfo)
          this.addStructuredData();
      });
  }

  openGallery() {
    this.galleryIsHidden = false;
    this.analytics.open_gallery();
  }

  openGalleryB(img) {
    if (this.deviceService.device === "desktop" || this.deviceService.device === "tablet") {
      this.img = img;
      this.galleryIsHidden = false;
      this.analytics.open_gallery();
    }
  }

  closeGallery() {
    this.galleryIsHidden = true;
  }

  goToCalendar() {
    let top = this.element.nativeElement.querySelector('#price-box').getBoundingClientRect().top;
    this.scrollService.scrollTo(top - 60);
  }

  checkAvailability(checkAvailability: CheckAvailability) {
    this.check = checkAvailability;
    if (checkAvailability.date != null) {
      let day = checkAvailability.date.getDate();
      let month = checkAvailability.date.getMonth() + 1;
      let year = checkAvailability.date.getFullYear();
      this.check.dateStr = year + '-' + month + '-' + day;
    }
    this._spinner.setHidden(false);
    this.productService.checkAvailability(this.product.id, checkAvailability, 'B').subscribe(options => {
      this._spinner.setHidden(true);
      let offset = this.element.nativeElement.querySelector('#availability-legend').getBoundingClientRect().top;
      let offsetHeight = this.element.nativeElement.querySelector('#availability-legend').offsetHeight;
      this.scrollService.scrollTo(offset + offsetHeight - 98);
      this.options = options.map(option => {
        option.date = checkAvailability.date;
        return option;
      });
      if (this.options != null && this.options.length > 0) {
        this.analytics.eCommerce_productDetails(this.product, this.options);
      }
    });
  }

  /**
  * 
  * @param event height of product price component
  */
  getHeightFromProductPrice(event: number) {
    this.productPriceHeight = event;
    this.getHeightCalcule();
    this.getFixedViewportOffset(this.productPriceHeight);
  }

  /**
   * height of the initial position
   */
  private getHeightCalcule() {
    let initialViewportOffset = this.element.nativeElement.querySelector('#product-price').getBoundingClientRect().top;
    let mapHeight = this.element.nativeElement.querySelector('#map-point').getBoundingClientRect().top;
    let currentScroll = this.windowScroll.getCurrentScroll();
    this.initialOffsetFromTop = initialViewportOffset + currentScroll;
    this.mapOffset = mapHeight + currentScroll - 800;
    let containerRef = this.element.nativeElement.querySelector('.product-additional').getBoundingClientRect().top;
    this.initialOffsetFromTop2 = containerRef + currentScroll + 444;
  }

  private getFixedViewportOffset(productPriceHeight) {
    this.fixedViewportOffset = 444 + productPriceHeight;
  }

  private handleScroll(currentScroll) {
    if (currentScroll > this.fixedViewportOffset) {
      this.showMenuButton = true;
      if (currentScroll > this.initialOffsetFromTop2) {
        this.showSeeOptions = true;
      } else {
        this.showSeeOptions = false;
      }
    } else if (currentScroll == 0) {
      this.showMenuButton = false;
      this.showSeeOptions = false;
    } else if (this.showMenuButton) {
      let currentOffsetFromTop = currentScroll + this.element.nativeElement.querySelector('#product-price').getBoundingClientRect().top;
      if (currentOffsetFromTop <= this.initialOffsetFromTop) {
        this.showMenuButton = false;
      }
    } else {
      this.showMenuButton = false;
      this.showSeeOptions = false;
    }
  }

  toggleAnnounce(event, forceFalse = false) {
    this.showingAnnounce = !this.showingAnnounce;
    if (forceFalse)
      this.showingAnnounce = false;
    if (this.showingAnnounce) {
      event.stopPropagation();
    }
  }

  changePrice(e) {
    this.highPrice = e.highPrice;
  }

  private getHreflangs(id: number) {
    let _hlangs: HrefLangDefinition[] = []
    this.productService.getHreflang(id).subscribe(hrefs => {
      hrefs.map(href => {
        let hrefLocale = href.locale.split("_")[0];
        let name = href.url;
        let cityName = href.locationName;


        let hreflang_: HrefLangDefinition = {
          rel: "alternate",
          href: `${AppSettings.BASE_DOMAIN}/${hrefLocale}/${href.locationId}/${cityName}/${id}/${name}`,
          hreflang: `${hrefLocale}`
        }
        this.hlang.addTags(hreflang_);
        _hlangs.push(hreflang_);
      });
      let currentUrl = this.router.url.split('?')[0];
      let res = this.checkUrl.compareUrl(_hlangs, this.router.url.split('?')[0]);
      let canonicalUrl = currentUrl;
      if (res != null) {
        canonicalUrl = res.url;
        if (res.status == 301) {
          if (this.isNode)
            this.response.redirect(301, res.url);
          else {
            const urlPart = res.url.split("/");
            const url = this.router.createUrlTree([`/${urlPart[3]}`, urlPart[4], urlPart[5], urlPart[6], urlPart[7]], { relativeTo: this.route }).toString();
            this.location.go(url);
            // window.location.href = res.url;
          }
        } else if (res.status == 404) {
          this.router.navigate(['/oops/404']);
        }
      }
      this.seoService.createLinkForCanonicalURL(canonicalUrl);
    });
  }

  private addStructuredData() {
    if (!this.structuredDataReady) {
      var baseDomain = AppSettings.BASE_DOMAIN.substring(0, AppSettings.BASE_DOMAIN.length - 1);
      let imageUrl = '';
      if (this.product.main) {
        imageUrl = AppSettings.CSS_CE_IMAGES + this.product.main;
      } else if (this.product.gallery[0] && this.product.gallery[0]['name']) {
        imageUrl = AppSettings.CSS_CE_IMAGES + this.product.gallery[0]['name'];
      }
      let script = this.renderer.createElement(this.element.nativeElement, 'script');
      this.renderer.setElementAttribute(script, 'type', 'application/ld+json');
      this.renderer.createText(script, `{
        "@context": "http://schema.org/",
        "@type": "Product",
        "name": "` + this.product.name + `",
        "url": "` + baseDomain + this.router.url.split('?')[0] + `",
        "image": "` + imageUrl + `",
        "description": "` + this.product['description'] + `",
        "brand": {
          "@type": "Brand",
          "name": "City Expert"
        },
        "offers": {
          "@type": "AggregateOffer",
          "availability": "http://schema.org/InStock",
          "lowPrice": "`+ this.product.bestPriceInfo.bestPrice + `",
          "priceCurrency": "${AppSettings.currencies[this.product.bestPriceInfo.currencyId]}"
        }
      }`);
      this.structuredDataReady = true;
    }
  }

  nextImage() {
    this.isAnimated = false;
    this.animationPrev = false;
    this.animationMax = false;
    this.animationMin = false;
    if (this.currentIndex != this.images.length - 1) {
      this.animationNext = true;
      this.currentIndex++;
      this.imagesHeader[0] = this.images[this.currentIndex];
      this.imagesHeaderMicro[0] = this.imagesMicro[this.currentIndex];
      // this.analytics.slide_product_image();
    } else {
      this.animationMax = true;
    }
  }

  previousImage() {
    this.isAnimated = false;
    this.animationNext = false;
    this.animationMax = false;
    this.animationMin = false;
    if (this.currentIndex != 0) {
      this.animationPrev = true;
      this.currentIndex--;
      this.imagesHeader[0] = this.images[this.currentIndex];
      this.imagesHeaderMicro[0] = this.imagesMicro[this.currentIndex];
      // this.analytics.slide_product_image();
    } else {
      this.imagesHeader[0] = this.imagesHeader[0];
      this.imagesHeaderMicro[0] = this.imagesMicro[0];
      this.animationMin = true;
    }
  }

  swipe(currentIndex: number, action = this.SWIPE_ACTION.RIGHT) {
    if (action === this.SWIPE_ACTION.RIGHT && this.deviceService.device !== "desktop" && this.deviceService.device !== "tablet")
      this.previousImage();

    if (action === this.SWIPE_ACTION.LEFT && this.deviceService.device !== "desktop" && this.deviceService.device !== "tablet")
      this.nextImage();
  }

  /** ADDITIONAL METHODS */

  openCruiseShip() {
    this.cruiseIsOpened = true;
    this.analytics.click_on_cruise();
  }
  closeCruiseShip() {
    this.cruiseIsOpened = false;
  }

  onEvent(event) {
    event.stopPropagation();
  }
}